import { Component, EventEmitter, Input, Output } from '@angular/core';
import { initials, memberLabel } from '../community-format';

/** Space header: name, counts and actions. Actions are handled by the parent. */
@Component({
  selector: 'app-cm-chat-header',
  template: `
    <header class="cm-top">
      <button type="button" class="cm-tbtn cm-spaces" (click)="spaces.emit()" aria-label="Show spaces"><mat-icon>menu</mat-icon></button>
      <span class="cm-av" aria-hidden="true">
        <img *ngIf="avatarUrl; else ini" [src]="avatarUrl" alt="" /><ng-template #ini>{{ initialsText }}</ng-template>
      </span>
      <div class="cm-title">
        <h1>{{ channel?.channelName }}</h1>
        <div class="cm-meta">
          <span *ngIf="onlineCount !== null"><i class="cm-dot"></i>{{ onlineCount }} online</span>
          <span *ngIf="channel?.memberCount > 0">{{ members }}</span>
          <span class="cm-hide-sm">{{ channel?.channelVisibility === 'private' ? 'Private' : 'Public' }}<ng-container *ngIf="channel?.teamName"> · {{ channel.teamName }}</ng-container></span>
        </div>
      </div>
      <div class="cm-tools">
        <ng-content></ng-content>
        <button type="button" class="cm-tbtn" *ngIf="channel?.channelVisibility === 'public'" title="Share space" aria-label="Share space"
          (click)="share.emit(); $event.stopPropagation()"><mat-icon>share</mat-icon></button>
        <button type="button" class="cm-tbtn" *ngIf="!isCreator" [class.starred]="channel?.isStarred" [attr.aria-pressed]="!!channel?.isStarred"
          [title]="channel?.isStarred ? 'Starred' : 'Star'" (click)="star.emit()">
          <mat-icon>{{ channel?.isStarred ? 'star' : 'star_border' }}</mat-icon></button>
        <button type="button" class="cm-tbtn" [class.on]="panelOpen" [attr.aria-pressed]="panelOpen"
          [title]="panelOpen ? 'Hide details' : 'Show details'" (click)="togglePanel.emit()"><mat-icon>view_sidebar</mat-icon></button>
        <ng-container *ngIf="!isCreator">
          <button type="button" class="cm-tbtn" [matMenuTriggerFor]="more" title="More" aria-label="More actions"><mat-icon>more_vert</mat-icon></button>
          <mat-menu #more="matMenu" class="cm-menu">
            <button mat-menu-item (click)="join.emit()"><mat-icon>{{ joinAction.icon }}</mat-icon>{{ joinAction.label }}</button>
            <button mat-menu-item (click)="report.emit()"><mat-icon>report</mat-icon>Report space</button>
          </mat-menu>
        </ng-container>
      </div>
    </header>
  `,
  styles: [`
    .cm-top { display: flex; align-items: center; gap: 12px; padding: 12px 18px; border-bottom: 1px solid var(--cm-line); }
    .cm-av { width: 38px; height: 38px; flex: none; display: grid; place-items: center; overflow: hidden; border-radius: var(--cm-shape);
      background: var(--cm-sec-bg); color: var(--cm-sec-fg); font-weight: 700; font-size: 13px; }
    .cm-av img { width: 100%; height: 100%; object-fit: cover; background: #fff; }
    .cm-title { min-width: 0; }
    h1 { margin: 0; font-size: var(--cm-fs-title); line-height: 1.2; font-weight: 700; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
    .cm-meta { display: flex; flex-wrap: wrap; gap: 10px; font-size: 12px; color: var(--cm-muted); }
    .cm-dot { display: inline-block; width: 7px; height: 7px; margin-right: 4px; border-radius: 50%; background: #2fbf71; }
    .cm-tools { margin-left: auto; display: flex; align-items: center; gap: 2px; position: relative; }
    .cm-tbtn { display: grid; place-items: center; width: 36px; height: 36px; border: 0; border-radius: 8px; background: none;
      color: var(--cm-muted); cursor: pointer; }
    .cm-tbtn:hover { background: var(--cm-raise); color: var(--cm-main-fg); }
    .cm-tbtn.on { color: var(--cm-accent); background: var(--cm-tint); }
    .cm-tbtn.starred { color: var(--cm-star); }
    .cm-tbtn:focus-visible { outline: 2px solid var(--cm-accent); outline-offset: 2px; }
    .cm-spaces { display: none; }
    @media (max-width: 720px) { .cm-spaces { display: grid; } .cm-hide-sm { display: none; } .cm-top { padding: 10px 12px; gap: 8px; } }
  `],
})
export class CmChatHeaderComponent {
  @Input() channel: any;
  @Input() avatarUrl: string | null = null;
  @Input() isCreator = false;
  @Input() panelOpen = false;
  /** null hides the count (presence off). */
  @Input() onlineCount: number | null = null;

  @Output() share = new EventEmitter<void>();
  @Output() star = new EventEmitter<void>();
  @Output() togglePanel = new EventEmitter<void>();
  @Output() join = new EventEmitter<void>();
  @Output() report = new EventEmitter<void>();
  @Output() spaces = new EventEmitter<void>();

  get initialsText(): string {
    return initials(this.channel?.channelName);
  }

  /** Joined public spaces can be left; a private space is only ever left by the org. */
  get joinAction(): { icon: string; label: string } {
    if (!this.channel?.isJoined) return { icon: 'group_add', label: 'Join space' };
    return this.channel?.channelVisibility === 'public'
      ? { icon: 'logout', label: 'Leave space' }
      : { icon: 'check', label: 'Joined' };
  }

  get members(): string {
    return memberLabel(this.channel?.memberCount);
  }
}
